import { FC, useCallback, useMemo } from "react";
import { Card } from "@src/ui/card";
import RichTextEditor from "@src/components/libraryItemDetailPages/course/RichTextEditor";
import { emptyTranslationSet } from "@utils/prompts";
import { useTranslation } from "@utils/translationSets";
import { TranslationModeContext } from "@src/components/libraryItemDetailPages/course/contexts/TranslationModeContextProvider";
import { useContext } from "react";
import { useAtomValue } from "jotai";
import { trainingResourceVersionAtom } from "../atoms";
import { useDispatchSetRichTextDocumentContentAction } from "../hooks/useDispatchSetRichTextDocumentContentAction";
import { useTrainingResourceContentPermissions } from "../TrainingResourcePageHeader";
import useTrainingResourceViewModeQueryParams from "../hooks/useTrainingResourceViewModeQueryParams";
import { cn } from "@src/ui/lib/utils";
import TrainingResourceToggleView from "@src/components/libraryItemDetailPages/resource/build/TrainingResourceToggleView";
import ScrollableFlexGrow from "@src/components/ui/flexBox/ScrollableFlexGrow";
import TrainingResourceTitleInput from "@src/components/libraryItemDetailPages/resource/build/TrainingResourceTitleInput";
import useSyncActiveClient from "@src/components/libraryItemDetailPages/course/hooks/useSyncActiveClient";
import useVersionQueryParams from "@src/components/libraryItemDetailPages/hooks/useVersionQueryParams";

// Fixes a tiptap editor bug where imgs are not
// rendered when imgs are wrapped in p tags.
const removeImageParentParagraphs = (content: string): string => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = content;

  const paragraphs = tempDiv.querySelectorAll("p");
  paragraphs.forEach((p) => {
    const images = p.querySelectorAll("img");
    if (images.length > 0) {
      images.forEach((img) => {
        // Add mouseover event handler to trigger click
        img.addEventListener("mouseover", () => {
          img.click();
        });
        p.parentNode?.insertBefore(img, p);
      });
      if (!p.textContent?.trim()) {
        p.remove();
      }
    }
  });

  return tempDiv.innerHTML;
};

// Function to extract body content from full HTML document
export const extractBodyContent = (htmlContent: string): string => {
  // Process images before any other content processing
  const processedContent = removeImageParentParagraphs(htmlContent);

  // If it's not a full HTML document, return the processed content
  if (!processedContent.includes("<!DOCTYPE html>")) {
    return processedContent;
  }

  // Create a temporary div to parse the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = processedContent;

  // Extract everything after the first h2 (skipping the style tags and other head content)
  const firstH2 = tempDiv.querySelector("h2") as HTMLHeadingElement | null;
  if (!firstH2) {
    return processedContent;
  }

  // Get all content starting from the first h2
  let content = "";
  let currentElement: Element | null = firstH2;
  while (currentElement) {
    content += currentElement.outerHTML;
    currentElement = currentElement.nextElementSibling;
  }

  return content;
};

const TrainingResourceRichTextEditor: FC = () => {
  const translationModeContext = useContext(TranslationModeContext);
  const dispatchTrainingResourceAction =
    useDispatchSetRichTextDocumentContentAction();
  const trainingResourceVersion = useAtomValue(trainingResourceVersionAtom);
  const currentContent =
    trainingResourceVersion?.richTextDocument?.content || emptyTranslationSet();
  const currentContentTranslation = useTranslation(
    currentContent,
    translationModeContext?.currentLang || "en",
  );
  const richTextDocumentId = trainingResourceVersion?.richTextDocument?.id;
  const contentPermissions = useTrainingResourceContentPermissions();
  const { trainingResourceViewMode } = useTrainingResourceViewModeQueryParams();
  useSyncActiveClient();
  const { version } = useVersionQueryParams();

  const handleContentChange = useCallback(
    (html: string) => {
      if (!richTextDocumentId) return;
      dispatchTrainingResourceAction({
        richTextDocumentId: richTextDocumentId,
        newText: html,
      });
    },
    [richTextDocumentId, dispatchTrainingResourceAction],
  );
  const originalContent = useMemo(
    () => extractBodyContent(currentContentTranslation.text ?? ""),
    [currentContentTranslation.text],
  );

  return (
    <div className="relative flex flex-1 flex-col self-stretch">
      <ScrollableFlexGrow
        flex={1}
        alignSelf="stretch"
        innerStyle={{
          display: "flex",
          alignItems: "center",
          padding: 48,
        }}
      >
        <Card
          className={cn(
            "flex flex-col transition-all duration-300 ease-in-out",
            trainingResourceViewMode === "mobile-view"
              ? "w-[420px] rounded-[30px]"
              : "w-[800px]",
          )}
        >
          <div
            className={cn(
              "flex flex-1 flex-col",
              version === "published" && "pointer-events-none",
              trainingResourceViewMode === "mobile-view"
                ? "min-h-[800px] p-6"
                : "min-h-[1200px] p-20",
            )}
          >
            <TrainingResourceTitleInput />
            <RichTextEditor
              text={originalContent}
              hasEditContentPermission={
                contentPermissions?.hasEditContentPermission ?? false
              }
              onChange={handleContentChange}
              placeholder="Start typing your accessible document..."
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className="rich-text-opus-doc-editor"
              disableTextVariants
              enableMathematics={true}
              enableGapcursor={true}
            />
          </div>
        </Card>
      </ScrollableFlexGrow>
      <TrainingResourceToggleView />
    </div>
  );
};

export default TrainingResourceRichTextEditor;
